import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { capture } from '../../utils/analytics';
import {
  isMobile,
  isIOSSafari,
  isIOS,
  isAndroid,
  getInstallContext,
} from '../../utils/pwaDetection';
import {
  shouldShowBanner,
  markShown,
  markDismissed,
  getDebugSnapshot,
  recordVisit,
} from '../../utils/installPromptManager';
import {
  hasNativePrompt,
  triggerNativePrompt,
} from '../../utils/installPromptCapture';
import {
  platformLabel,
  decideCtaAction,
  buildShownProps,
  buildDismissProps,
  buildCtaProps,
  writeInstallSource,
} from './installBannerLogic';
import IOSInstallModal from './IOSInstallModal';
import './InstallBanner.css';

export default function InstallBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [reason, setReason] = useState(null);
  const [iosModalOpen, setIosModalOpen] = useState(false);

  useEffect(() => {
    if (!isMobile()) return;
    // CSS hides the banner at viewport widths >= 769px (see InstallBanner.css).
    // iPad in landscape passes the UA `isMobile()` check but renders at
    // ~1024px wide, so without this viewport guard `markShown()` would fire
    // for an invisible banner — burning the once-per-day cap silently.
    if (typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        !window.matchMedia('(max-width: 768px)').matches) {
      return;
    }
    // Must run before shouldShowBanner so visitCount/firstVisitAt reflect
    // this visit (trackPwaSession runs later on a 1s timer in App.jsx,
    // and recordVisit is idempotent within a calendar day).
    recordVisit();
    const decision = shouldShowBanner();
    if (!decision.show) return;
    setReason(decision.reason);
    setVisible(true);
  }, []);

  useEffect(() => {
    if (!visible) return;
    markShown();
    capture(
      'pwa_banner_shown',
      buildShownProps({
        reason,
        snapshot: getDebugSnapshot(),
        platform: platformLabel({ ios: isIOS(), android: isAndroid() }),
      }),
    );
  }, [visible, reason]);

  const handleDismiss = useCallback(() => {
    const snapshot = getDebugSnapshot();
    markDismissed();
    capture('pwa_banner_dismissed', buildDismissProps({ snapshot }));
    setVisible(false);
  }, []);

  const handleCta = useCallback(async () => {
    const platform = platformLabel({ ios: isIOS(), android: isAndroid() });
    const native = hasNativePrompt();
    capture(
      'pwa_banner_cta_clicked',
      buildCtaProps({
        platform,
        hasNativePrompt: native,
        installContext: getInstallContext(),
      }),
    );

    const action = decideCtaAction({
      iosSafari: isIOSSafari(),
      ios: isIOS(),
      hasNativePrompt: native,
    });

    if (action.type === 'open_ios_modal') {
      setIosModalOpen(true);
      return;
    }
    if (action.type === 'trigger_native_prompt') {
      // Only attribute to banner_cta on the path that can actually trigger
      // an `appinstalled` event. On `no_prompt_available` we must not write
      // a source, or a later install via browser menu would be misattributed.
      writeInstallSource('banner_cta');
      const { outcome } = await triggerNativePrompt();
      // User bailed out of the Chrome native prompt. Treat as a banner
      // dismissal so the 2-day cooldown applies — otherwise lastShownAt
      // only blocks for the current UTC day and we'd badger the same user
      // tomorrow even though they already said no to install.
      if (outcome === 'dismissed') {
        markDismissed();
      }
      setVisible(false);
      return;
    }
    // Android browsers without a captured `beforeinstallprompt` (e.g. Samsung
    // Internet, Firefox, or Chrome before BIP fires): we have no way to drive
    // an install flow from the CTA. Hide the banner + mark dismissed so the
    // user gets immediate feedback and we don't re-show tomorrow.
    capture('pwa_cta_clicked_no_prompt', { platform });
    markDismissed();
    setVisible(false);
  }, []);

  // Single, stable render position for the modal — keeping it conditional
  // on `visible` would unmount/remount it whenever the banner toggles
  // (e.g. user dismisses the banner after opening the modal), re-firing
  // `pwa_ios_modal_opened` on remount and inflating funnel counts.
  return (
    <>
      {visible && (
        <div
          className="tm-install-banner"
          role="region"
          aria-label={t('pwa.banner.text', 'Install TraderMap on your phone for instant access')}
          aria-hidden={iosModalOpen ? 'true' : undefined}
          data-testid="install-banner"
        >
          <div className="tm-install-banner__icon" aria-hidden="true">
            <img src="/web-app-manifest-192x192.png" alt="" width="28" height="28" />
          </div>
          <div className="tm-install-banner__text">
            {t('pwa.banner.text', 'Install TraderMap on your phone for instant access')}
          </div>
          <button
            type="button"
            className="tm-install-banner__cta"
            onClick={handleCta}
            data-testid="install-banner-cta"
          >
            {t('pwa.banner.cta', 'Install')}
          </button>
          <button
            type="button"
            className="tm-install-banner__close"
            aria-label={t('toast.close', 'Dismiss')}
            onClick={handleDismiss}
            data-testid="install-banner-dismiss"
          >
            ×
          </button>
        </div>
      )}
      <IOSInstallModal
        open={iosModalOpen}
        onClose={() => setIosModalOpen(false)}
      />
    </>
  );
}
